import { useZero } from "@rocicorp/zero/react";
import { useMemo } from "react";
import { sharedMutators } from "../../../shared/mutators";
import { queries } from "../../../shared/queries";
import { useCurrentUserGroups } from "../../hooks/useCurrentUserGroups";
import { useDefaultConnectorGrants } from "../../hooks/useDefaultConnectorGrants";
import { useHasAccess } from "../../hooks/useHasAccess";
import { useQuery } from "../../hooks/useQuery";
import { ConnectorsBar } from "../ConnectorsBar";

type Props = {
  personalChatId: string;
};

export function PersonalChatConnectorsBar({ personalChatId }: Props) {
  const z = useZero();
  const canCreateApp = useHasAccess("tenant:connected_app_create");
  const defaultConnectorGrants = useDefaultConnectorGrants(undefined);
  const { currentUser } = useCurrentUserGroups();

  const [personalChat] = useQuery(queries.loadPersonalChat({ personalChatId }));

  const connectorRows = useMemo(
    () => personalChat?.appUsages ?? [],
    [personalChat?.appUsages]
  );
  const connectedAppIds = useMemo(
    () => new Set(connectorRows.map((r) => r.connectedAppId)),
    [connectorRows]
  );

  const connectors = useMemo(
    () =>
      connectorRows.flatMap((row) =>
        row.connectedApp ? [row.connectedApp] : []
      ),
    [connectorRows]
  );

  const associate = (connectedAppId: string) =>
    z.mutate(
      sharedMutators.associatePersonalChatWithConnectedApp({
        personalChatId,
        connectedAppId,
      })
    );

  const dissociate = (connectedAppId: string) =>
    z.mutate(
      sharedMutators.dissociatePersonalChatFromConnectedApp({
        personalChatId,
        connectedAppId,
      })
    );

  return (
    <ConnectorsBar
      parentNoun="chat"
      canWrite={!!currentUser}
      canCreateApp={canCreateApp}
      defaultConnectorGrants={defaultConnectorGrants}
      connected={connectors}
      excludeIds={connectedAppIds}
      associate={associate}
      remove={dissociate}
      menuMargin={6}
      subtleAddButton
    />
  );
}
